import { useEffect, useState } from "react";
import { Check, ChevronRight, Copy, Minus } from "lucide-react";

import {
  type Reference,
  type ReferenceField,
  type ReferenceRoot,
  getReference,
} from "@/lib/scoring/api";

import styles from "./ProgramCanvas.module.css";

/**
 * Everything an author may write, and what is in scope while they write it.
 *
 * Generated, never typed here: the server assembles it from `scoring.reference`
 * — which is tested against the pipeline itself — plus the same tables the
 * publish gate checks against. So what this page offers and what the engine
 * will actually hand you cannot drift apart.
 *
 * Two things this page does that its predecessor did not, and both were bugs
 * rather than omissions:
 *
 * It shows **both spellings of every root**. An expression is evaluated against
 * the scope, so it reads `derived.x`; a function is handed that scope, so it
 * reads `ctx.derived.x`. The old page printed only the second, and because a
 * path that misses resolves to null rather than raising, an author who copied
 * it into an expression got a rule that silently never fired.
 *
 * It **follows the node you have open**. The scope of a rule and the scope of
 * an action genuinely differ — a rule is handed the command and no stats, an
 * action is handed the stats and no command — so a page that describes all four
 * at once is a page you have to read past.
 */

/** What each canvas kind is called in the reference. Commands have no function
 *  of their own, so opening one leaves the last real kind showing. */
const CONTRACTED = new Set(["validation", "compute", "trigger", "context"]);

/** "a rule", but "an action". The kind labels come from the server, so the
 *  article has to be worked out rather than written into the sentence. */
const article = (label: string) =>
  `${/^[aeiou]/i.test(label) ? "an" : "a"} ${label.toLowerCase()}`;

export function ReferencePane({
  programId,
  kind,
}: {
  programId: string;
  kind?: string | null;
}) {
  const [reference, setReference] = useState<Reference | null>(null);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState("");
  /** A kind picked by hand, and the open node's kind at the moment it was
   *  picked. Keeping the second half is what lets the rule below be a plain
   *  derivation rather than an effect that races the selection. */
  const [picked, setPicked] = useState<{ kind: string; against: string | null } | null>(null);

  useEffect(() => {
    let stale = false;
    getReference(programId)
      .then((loaded) => {
        if (!stale) setReference(loaded);
      })
      .catch(() => {
        if (!stale) setFailed(true);
      });
    return () => {
      stale = true;
    };
  }, [programId]);

  /**
   * Which kind the page is describing.
   *
   * The node you have open wins, so walking the canvas walks the reference with
   * it. Picking a kind by hand holds — but only until you open a node of a
   * different kind, which is the gesture that most clearly means "now tell me
   * about this one". A command has no function of its own, so opening one
   * leaves the last real kind showing rather than blanking the page.
   */
  const following = kind && CONTRACTED.has(kind) ? kind : null;
  const active =
    picked && picked.against === following ? picked.kind : (following ?? "validation");

  if (failed) return <p className={styles.empty}>Could not load the reference.</p>;
  if (!reference) return <p className={styles.empty}>Loading…</p>;

  const contract = reference.kinds.find((k) => k.kind === active) ?? reference.kinds[0];
  const rootsByName = new Map(reference.roots.map((root) => [root.name, root]));
  const kinds = new Map(reference.fixtureKinds.map((k) => [k.code, k.label]));
  const hunt = query.trim().toLowerCase();
  const hit = (...text: (string | null | undefined)[]) =>
    !hunt || text.some((one) => (one ?? "").toLowerCase().includes(hunt));
  const hitField = (field: ReferenceField) => hit(field.name, field.type, field.what);

  /* An expression is what most rules are, so it leads for a rule and is filed
     under the function that can also do the job for everything else. */
  const expressionLeads = active === "validation";

  const language = reference.language;
  const showLanguage =
    !hunt ||
    language.operators.some((group) => group.operators.some((op) => hit(op.symbol, op.what))) ||
    language.functions.some(hitField) ||
    language.nullRules.some((rule) => hit(rule)) ||
    language.examples.some((one) => hit(one.what, one.code)) ||
    hit("expression");

  const showFunctions =
    !hunt ||
    reference.functions.rules.some((rule) => hit(rule)) ||
    reference.functions.limits.some((limit) => hit(limit.what, limit.value)) ||
    reference.functions.refusals.some((one) => hit(one.what, one.why)) ||
    hit("function");

  const roots = reference.roots.filter(
    (root) => hit(root.name, root.what, root.note) || root.fields.some(hitField),
  );
  const commands = reference.commands.filter(
    (command) => hit(command.code, command.label) || command.fields.some(hitField),
  );
  const rowShapes = reference.rowShapes.filter(
    (shape) => hit(shape.label, shape.what, shape.rule) || shape.fields.some(hitField),
  );
  const stats = reference.stats.filter((stat) => hit(stat.key, stat.description, stat.rows));
  const configs = reference.configs.filter(
    (config) => hit(config.code, config.label) || Object.keys(config.values).some((k) => hit(k)),
  );
  const masters = reference.masters.filter(
    (master) => hit(master.code, master.label) || master.fields.some((f) => hit(f)),
  );
  const roles = reference.roles.filter((role) => hit(role));

  const nothing =
    hunt &&
    !showLanguage &&
    !showFunctions &&
    roots.length === 0 &&
    commands.length === 0 &&
    rowShapes.length === 0 &&
    stats.length === 0 &&
    configs.length === 0 &&
    masters.length === 0 &&
    roles.length === 0;

  return (
    <div className={styles.ref}>
      <label className={styles.refSearch}>
        <input
          value={query}
          placeholder="Search paths, stats, operators…"
          onChange={(event) => setQuery(event.target.value)}
        />
      </label>

      {/* Which kind is being described. A strip rather than a dropdown because
          all four fit and the point is to show that they differ. */}
      <div className={styles.refKinds} role="tablist" aria-label="Node kind">
        {reference.kinds.map((one) => (
          <button
            key={one.kind}
            type="button"
            role="tab"
            aria-selected={one.kind === active}
            data-on={one.kind === active || undefined}
            onClick={() => setPicked({ kind: one.kind, against: following })}
          >
            {one.label}
          </button>
        ))}
      </div>

      {nothing && <p className={styles.empty}>Nothing matches “{query}”.</p>}

      {!hunt && contract && (
        <section className={styles.refContract}>
          <code>{contract.signature}</code>
          <p>
            <b>Gives back</b> {contract.returns}
          </p>
          <p>
            <b>Writes</b> {contract.writes}
          </p>
        </section>
      )}

      {/* ---------------------------------------------------------- in scope */}
      {roots.length > 0 && contract && (
        <Group name={`In scope in ${article(contract.label)}`} count={roots.length} />
      )}
      {roots.map((root) => {
        const seen = contract?.scope.find((one) => one.root === root.name);
        return (
          <RootRow
            key={root.name}
            root={root}
            present={seen?.present ?? true}
            empty={seen?.empty ?? false}
            note={seen?.note || root.note}
            open={Boolean(hunt)}
            commands={root.name === "input" ? commands : undefined}
          />
        );
      })}

      {/* ------------------------------------------------ the small language */}
      {showLanguage && (
        <>
          <Group name="Writing an expression" />
          {!hunt && (
            <p className={styles.refLead}>
              {expressionLeads
                ? "Most rules are one line of this. It has no loops and no way to reach the host, which is why it needs no sandbox — when a rule needs a loop it needs a function instead."
                : "Only a Rule can be written this way. Everything else is a function."}
            </p>
          )}

          {language.operators.map((group) => {
            const operators = group.operators.filter((op) => hit(op.symbol, op.what));
            if (operators.length === 0) return null;
            return (
              <Block key={group.name} title={group.name} open={expressionLeads || Boolean(hunt)}>
                <dl className={styles.refDefs}>
                  {operators.map((op) => (
                    <div key={op.symbol}>
                      <dt>
                        <code>{op.symbol}</code>
                      </dt>
                      <dd>{op.what}</dd>
                    </div>
                  ))}
                </dl>
              </Block>
            );
          })}

          {language.functions.some(hitField) && (
            <Block title="Functions it can call" open={expressionLeads || Boolean(hunt)}>
              <p className={styles.refNote}>
                These are all of them. There is nothing else to call, which is most of
                why an expression is safe by construction.
              </p>
              <dl className={styles.refDefs}>
                {language.functions.filter(hitField).map((fn) => (
                  <div key={fn.name}>
                    <dt>
                      <Path text={fn.type} />
                    </dt>
                    <dd>{fn.what}</dd>
                  </div>
                ))}
              </dl>
            </Block>
          )}

          {language.nullRules.some((rule) => hit(rule)) && (
            <Block title="Four things that surprise people" open={expressionLeads || Boolean(hunt)}>
              <ul className={styles.refList}>
                {language.nullRules
                  .filter((rule) => hit(rule))
                  .map((rule) => (
                    <li key={rule}>{rule}</li>
                  ))}
              </ul>
            </Block>
          )}

          {language.examples.some((one) => hit(one.what, one.code)) && (
            <Block title="Worked examples" open={expressionLeads || Boolean(hunt)}>
              {language.examples
                .filter((one) => hit(one.what, one.code))
                .map((one) => (
                  <figure key={one.code} className={styles.refExample}>
                    <figcaption>{one.what}</figcaption>
                    <Path text={one.code} block />
                  </figure>
                ))}
            </Block>
          )}
        </>
      )}

      {/* ---------------------------------------------------------- functions */}
      {showFunctions && (
        <>
          <Group name="Writing a function" />
          {reference.functions.rules.some((rule) => hit(rule)) && (
            <Block title="The rules" open={!expressionLeads || Boolean(hunt)}>
              <ul className={styles.refList}>
                {reference.functions.rules
                  .filter((rule) => hit(rule))
                  .map((rule) => (
                    <li key={rule}>{rule}</li>
                  ))}
              </ul>
            </Block>
          )}
          {reference.functions.limits.some((limit) => hit(limit.what, limit.value)) && (
            <Block title="What one call may spend" open={Boolean(hunt)}>
              <dl className={styles.refDefs}>
                {reference.functions.limits
                  .filter((limit) => hit(limit.what, limit.value))
                  .map((limit) => (
                    <div key={limit.what}>
                      <dt>{limit.what}</dt>
                      <dd>{limit.value}</dd>
                    </div>
                  ))}
              </dl>
            </Block>
          )}
          {reference.functions.refusals.some((one) => hit(one.what, one.why)) && (
            <Block
              title="What publishing will refuse"
              count={reference.functions.refusals.length}
              open={Boolean(hunt)}
            >
              <p className={styles.refNote}>
                Caught when you publish, not when a match is live.
              </p>
              <dl className={styles.refDefs}>
                {reference.functions.refusals
                  .filter((one) => hit(one.what, one.why))
                  .map((one) => (
                    <div key={one.what}>
                      <dt>
                        <code>{one.what}</code>
                      </dt>
                      <dd>{one.why}</dd>
                    </div>
                  ))}
              </dl>
            </Block>
          )}
        </>
      )}

      {/* ------------------------------------------------- rows an action owes */}
      {rowShapes.length > 0 && (
        <>
          <Group name="Rows an Action returns" count={rowShapes.length} />
          <p className={styles.refLead}>
            An Action returns rows and the engine writes them. Each table wants its
            own shape, and a row that misses it fails the write.
          </p>
          {rowShapes.map((shape) => (
            <Block
              key={shape.target}
              title={shape.label}
              aside={shape.target}
              open={active === "trigger" || Boolean(hunt)}
            >
              <p className={styles.refNote}>{shape.what}</p>
              {shape.rule && <p className={styles.refRule}>{shape.rule}</p>}
              <Fields fields={shape.fields.filter(hitField)} />
              <figure className={styles.refExample}>
                <figcaption>One row</figcaption>
                <Path text={shape.example} block />
              </figure>
            </Block>
          ))}
        </>
      )}

      {/* -------------------------------------------------------------- stats */}
      {stats.length > 0 && (
        <>
          <Group name="Stats this sport registers" count={stats.length} />
          <p className={styles.refLead}>
            An Action may only write keys it declares it owns, and only keys from
            this list. Declared in sport setup, not here.
          </p>
          {stats.map((stat) => (
            <Block key={stat.key} title={stat.key} aside={stat.valueType.toLowerCase()} mono>
              {stat.description && <p className={styles.refNote}>{stat.description}</p>}
              <dl className={styles.refDefs}>
                <div>
                  <dt>Rows</dt>
                  <dd>{stat.rows}</dd>
                </div>
                {stat.unit && (
                  <div>
                    <dt>Unit</dt>
                    <dd>{stat.unit}</dd>
                  </div>
                )}
                {stat.appliesTo.length > 0 && (
                  <div>
                    <dt>Formats</dt>
                    <dd>{stat.appliesTo.map((code) => kinds.get(code) ?? code).join(", ")}</dd>
                  </div>
                )}
              </dl>
              {stat.example != null && (
                <figure className={styles.refExample}>
                  <figcaption>One row</figcaption>
                  <Path text={JSON.stringify(stat.example)} block />
                </figure>
              )}
            </Block>
          ))}
        </>
      )}

      {/* ------------------------------------------------------------ formats */}
      {configs.length > 0 && (
        <>
          <Group name="Formats" count={configs.length} />
          <p className={styles.refLead}>
            What <code>{rootsByName.get("fixture")?.inExpression ?? "fixture"}.format</code>{" "}
            holds, depending on the format the fixture was created with.
          </p>
          {configs.map((config) => (
            <Block
              key={config.code}
              title={config.label}
              aside={config.appliesTo.map((code) => kinds.get(code) ?? code).join(", ") || "all"}
              open={Boolean(hunt)}
            >
              <dl className={styles.refDefs}>
                {Object.entries(config.values).map(([key, value]) => (
                  <div key={key}>
                    <dt>
                      <Path text={`fixture.format.${key}`} />
                    </dt>
                    <dd>{JSON.stringify(value)}</dd>
                  </div>
                ))}
              </dl>
            </Block>
          ))}
        </>
      )}

      {/* ------------------------------------------------------------ masters */}
      {masters.length > 0 && (
        <>
          <Group name="Master kinds" count={masters.length} />
          <p className={styles.refLead}>
            One list per kind under <code>masters</code>. The fields below are the
            record form&apos;s own, so they sit under <code>values</code>.
          </p>
          {masters.map((master) => (
            <Block
              key={master.code}
              title={master.label}
              aside={`masters.${master.code}`}
              open={Boolean(hunt)}
            >
              {master.fields.length === 0 ? (
                <p className={styles.refNote}>
                  No record-form fields — the standard ones above are all it carries.
                </p>
              ) : (
                <dl className={styles.refDefs}>
                  {master.fields.map((entry) => {
                    const [path, type] = entry.split(": ");
                    return (
                      <div key={entry}>
                        <dt>
                          <Path text={path} />
                        </dt>
                        <dd>{type}</dd>
                      </div>
                    );
                  })}
                </dl>
              )}
            </Block>
          ))}
        </>
      )}

      {/* -------------------------------------------------------------- roles */}
      {roles.length > 0 && (
        <>
          <Group name="Roles" count={roles.length} />
          <p className={styles.refLead}>
            Read them off <code>masters.&lt;kind&gt;[].roles</code>, or off{" "}
            <code>masters.officials[].role</code> for the appointed panel.
          </p>
          <div className={styles.refChips}>
            {roles.map((role) => (
              <Path key={role} text={role} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ pieces */

function Group({ name, count }: { name: string; count?: number }) {
  return (
    <div className={styles.group}>
      <span className={styles.groupName}>{name}</span>
      {count != null && <span className={styles.groupCount}>{count}</span>}
    </div>
  );
}

/**
 * A collapsible block.
 *
 * `open` is a key rather than a prop on purpose: when a search opens every
 * block, closing the search has to close them again, and `<details>` keeps its
 * own state once mounted. Remounting is the cheap honest way to reset it.
 */
function Block({
  title,
  aside,
  count,
  open,
  mono,
  children,
}: {
  title: string;
  aside?: string;
  count?: number;
  open?: boolean;
  mono?: boolean;
  children: React.ReactNode;
}) {
  return (
    <details className={styles.refBlock} open={open} key={String(open)}>
      <summary>
        <ChevronRight size={13} className={styles.refTwisty} />
        <strong data-mono={mono || undefined}>{title}</strong>
        {aside && <code>{aside}</code>}
        {count != null && <span className={styles.refCount}>{count}</span>}
      </summary>
      <div className={styles.refBody}>{children}</div>
    </details>
  );
}

/** One root, with both spellings in the head and its fields in the body. */
function RootRow({
  root,
  present,
  empty,
  note,
  open,
  commands,
}: {
  root: ReferenceRoot;
  present: boolean;
  empty: boolean;
  note: string;
  open: boolean;
  commands?: { code: string; label: string; fields: ReferenceField[] }[];
}) {
  const state = !present ? "off" : empty ? "empty" : "on";
  return (
    <details
      className={styles.refBlock}
      data-scope={state}
      open={open || undefined}
      key={String(open)}
    >
      <summary>
        <ChevronRight size={13} className={styles.refTwisty} />
        <span className={styles.refMark} data-scope={state} aria-hidden>
          {present && !empty ? <Check size={12} /> : <Minus size={12} />}
        </span>
        <strong data-mono>{root.inExpression}</strong>
        <code>{root.type}</code>
      </summary>
      <div className={styles.refBody}>
        <p className={styles.refNote}>{root.what}</p>
        {note && <p className={styles.refRule} data-scope={state}>{note}</p>}

        {/* The whole reason this page was rewritten. Set apart from the field
            table below rather than filed as its first two rows, because these
            are how you spell the root and those are what is inside it. */}
        <div className={styles.refSpell}>
          <span>In an expression</span>
          <Path text={root.inExpression} />
          <span>In a function</span>
          <Path text={root.inFunction} />
        </div>

        {root.fields.length > 0 && <Fields fields={root.fields} prefix={root.inExpression} />}

        {commands && commands.length > 0 && (
          <div className={styles.refNested}>
            <p className={styles.refNote}>
              What each command sends. These are the fields <code>input</code> holds
              when a rule on that command runs.
            </p>
            {commands.map((command) => (
              <Block key={command.code} title={command.label} aside={command.code}>
                <Fields fields={command.fields} prefix="input" />
              </Block>
            ))}
          </div>
        )}
      </div>
    </details>
  );
}

/** A field table. The name is the copyable thing, because it is what gets typed. */
function Fields({ fields, prefix }: { fields: ReferenceField[]; prefix?: string }) {
  if (fields.length === 0) return null;
  return (
    <dl className={styles.refDefs}>
      {fields.map((field) => (
        <div key={field.name}>
          <dt>
            <Path text={prefix ? `${prefix}.${field.name}` : field.name} label={field.name} />
          </dt>
          <dd>
            <span className={styles.refType}>{field.type}</span>
            {field.required === true && <span className={styles.refReq}>required</span>}
            {field.what && <span>{field.what}</span>}
            {field.example && (
              <span className={styles.refEg}>
                e.g. <code>{field.example}</code>
              </span>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * A path you can take with you.
 *
 * Every path on this page exists to be typed somewhere else, and retyping
 * `masters.officials[].role` by eye is how a rule ends up reading null forever.
 * `label` lets the chip show the short name while the clipboard gets the whole
 * path — the table already says which root it is under.
 */
function Path({ text, label, block }: { text: string; label?: string; block?: boolean }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1200);
    return () => window.clearTimeout(timer);
  }, [copied]);

  return (
    <button
      type="button"
      className={styles.refPath}
      data-block={block || undefined}
      data-copied={copied || undefined}
      title={`Copy ${text}`}
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(() => setCopied(true));
      }}
    >
      <code>{label ?? text}</code>
      {copied ? <Check size={11} /> : <Copy size={11} />}
    </button>
  );
}
